import { useMemo } from 'react';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import { moveZero } from '@/pages/intelligence/components/Chart/_config/topchartsChartUtils';
import ChartBubble from '@/pages/intelligence/components/ChartBubble';
import { LINE_GRID_TOP, LEGEND_BOTTOM } from '@/pages/intelligence/pages/TopCharts/_config/echarts';
import {
  handleSerieSymbolConfig,
  getRealValueOfSerie,
  ISpecialSeriesOption,
} from '@/pages/intelligence/utils/echartsUtils';
import { useIntl, getLocale } from 'umi';
import { getSortedDates } from '@intelligence/utils/commonUtils';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { xAxisHighLight } from '@intelligence/v3/utils/echarts';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { seriesBelongsToAxisMetric } from '@intelligence/pages/TopCharts/utils/multiValueMetricUtils';
// 以mobile榜单Rpd折线图为例，做成通用折线图组件
/** *********
 * 1、组装lineData传入，以metric指标为属性名，对象，格式：
 * {
 * download:[
 *    {
 *      cover: 'https://ogdb-cdn.intlgame.cn/image/702b69f4f52e81e53b14f8f5c1221095.jpg',  //游戏头像
 *     history: [
 *       {
 *         metric: 'download', //指标名称
 *         source: "appannie", //数据源平台
 *         time: "2023-01-01",
 *         value: "2.310072e+06",
 *       }
 *       ...
 *    ],
 *     id: "u6b8bc1256c162b3189da416f6a2d618a",  //游戏id
 *     name: "Odin Valhalla Rising", //游戏名称
 *    }
 *    ...
 * ],
 * revenue: [...],
 * rpd: [...],
 * }
 * 2、用户切换metric时，lineData切换到相应指标名称数据即可
 * 3、x轴 label 数据，lineXAxisData  以对应指标第一个游戏数据的history的time集合即可
 * 4、后续其他折线图，组装好标准数据，传入此hooks即可返回lineOption,再传入通用chart入口组件即可
 * 5、标准数据： lineData， metric
 * 6、非通用标准的折线图，得从外面组装好各项配置再传入此hooks,得到lineOption
 * **************/

const calcDefaultIsNaNeedMax = (lineDatas: { [key: string]: any[]; }, metric: string): boolean => {
  const lineData = lineDatas?.[metric] || [];
  // 是否有100%以上的
  const hasMoreThan100 = lineData?.some((item: any) => {
    const histories = item?.history || [];
    return histories?.some((subItem: any) => {
      const numValue = Number.parseFloat(subItem?.value);
      return !Number.isNaN(numValue) && numValue > 100;
    });
  });
  return hasMoreThan100;
};

interface IUseLineChartsProps {
  // 新增参数：hover图例时其他线条的透明度（0-1之间，默认不启用）
  legendHoverOpacity?: number;
  specialSeriesOption?: ISpecialSeriesOption;
  /** 是否保留业务页声明的 series 展示元数据；默认关闭以维持公共 Hook 原行为。 */
  shouldPreserveSeriesDisplayMeta?: boolean;
  [key: string]: any;
}

export default function useLineCharts(props: IUseLineChartsProps) {
  const {
    lineXAxisData, // x轴 label 数据
    xAxisAutoSort,
    lineSeriesData, // 折线 数据
    legendsCustomMade, // 图例
    yAxisFormatter, // y轴格式化函数
    splitNumber = 4, // y轴显示个数
    dateType = 'Day',
    secondTitleRight,
    topChartsType,
    imageType = 'mobile',
    titleNow = '',
    chartType = 'line',
    tooltipFormatter,
    tooltip,
    lineData = {},
    metric = '',
    setChartLoading,
    isPercentLine = false,
    isNaNeedMax = calcDefaultIsNaNeedMax(lineData, metric),
    isStep = false,
    ifNeedMoveZero = true,
    gridConfig = {},
    colors = ECHARTSCOLOR,
    topTitle,
    // hasMarkLine = false, // 是否需要markLine，显示since release按钮
    baseReleaseTime = '',
    earliestPeriod = '',
    yAxisConfig = {},
    ifHasOtherHeadContent,
    isStackLine, // 堆积折线图
    startDate,
    endDate,
    isTwoYAxis = false, // 是否为双Y轴数据
    visuailsMetricValues = [],
    valueFormatter,
    legendSelected,
    lineSourceIcon,
    lineSourceName,
    gameHighId = '',
    isCenter = false,
    data_source = ['appannie'],
    dataSource,
    useExternalAxisTitles = false,
    // 新增参数：hover图例时其他线条的透明度（0-1之间，默认不启用）
    legendHoverOpacity,
    specialSeriesOption,
    seriesNameFormatter,
    xAxisLabelFormatter,
    shouldMoveTooltipOthersLast,
    tooltipOthersNames,
    shouldPreserveSeriesDisplayMeta = false,
  } = props;

  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();
  // line-x轴label格式化
  // const lineFormatterXAxis = (value) => {
  //   return isxLabelDate ? formatCalendar(value, '', dateType) : value;
  // }

  // y轴label格式化
  const formatterYAxis = (value: any) => {
    // console.log('valuevalue,value', value);
    switch (topChartsType) {
      case 'stats-retentionmonthly-line':
      case 'stats-retentionmonthly-trend':
      case 'stats-retentionmonthly-trend':
        return `${newMathFormat(value)}%`;
      default:
        return isPercentLine
          ? `${newMathFormat(value)}%`
          : value * 1 < 10 && value > 0
          ? value
          : newMathFormat(value, true);
    }
    // return value < 10 ? value : mathFormat(value, true);
  };

  const formatterYAxisPercentLine = (value: any) => {
    return `${newMathFormat(value)}%`;
  };

  // 默认浮窗内容
  const lineFormatterTooltip = (params: any[]) => {
    // console.log('paramsparams', params);
    // Move zeros if necessary
    const temp = ifNeedMoveZero ? moveZero(params) : params;
    // Reorder: place True Data (scatter) rows immediately under their parent KPI line
    const isTrueDataParam = (p: any) => p?.componentSubType === 'scatter' && p?.data?.__isTrueData;
    const lineItems = (Array.isArray(temp) ? temp : [])?.filter((p: any) => !isTrueDataParam(p));
    const trueDataItems = (Array.isArray(temp) ? temp : [])?.filter(isTrueDataParam);
    const groupedByParent = new Map<string, any[]>();
    trueDataItems.forEach((p: any) => {
      const parent = p?.data?.__parentName || '';
      if (!groupedByParent.has(parent)) groupedByParent.set(parent, []);
      groupedByParent.get(parent)!.push(p);
    });
    const orderedParams: any[] = [];
    lineItems.forEach((p: any) => {
      orderedParams.push(p);
      const siblings = groupedByParent.get(p?.seriesName);
      if (siblings && siblings.length) {
        orderedParams.push(...siblings);
      }
    });
    const paramsTemp = orderedParams.length ? orderedParams : lineItems;
    return ChartBubble({
      data: paramsTemp,
      titleNow,
      topChartsType,
      imageType,
      chartType,
      dateType,
      isPercent: isPercentLine,
      topTitle,
      startDate,
      endDate,
      ifHasOtherHeadContent,
      visuailsMetricValues,
      valueFormatter,
      secondTitleRight,
      lineSourceIcon,
      lineSourceName,
      isCenter,
      data_source,
      dataSource,
      useSerieNameFormatter: !!seriesNameFormatter,
      serieNameFormatter: seriesNameFormatter,
      shouldMoveTooltipOthersLast,
      tooltipOthersNames,
    });
  };

  // 根据字符长度，返回padding宽度
  const getTextPadding = (text: string) => {
    const lengthNum = text?.length || 0;
    const guNum = locale === 'en-US' ? 10 : 8;
    const chenNum = locale === 'en-US' ? 8 : 13;
    const paddNum = lengthNum - guNum;
    if (paddNum > 0) {
      return chenNum * paddNum;
    }
    return 0;
  };

  // 获取 lineSeriesData
  const createLineSeriesItemData = (lineData: any, metric: string) => {
    const seriesTemp: any[] = [];
    try {
      if (lineData.hasOwnProperty(metric)) {
        const tempList = lineData[metric] || [];
        tempList?.forEach((item: any, index: number) => {
          const seriesColor = item?.color || colors[index % colors.length];
          seriesTemp.push({
            type: 'line',
            name: item.name,
            id: item?.id,
            step: isStep ?? false,
            data: item?.history?.map((item: any) => [item?.time, item?.value]) || [],
            showSymbol: false,
            symbolSize: 10,
            lineStyle: {
              type: 'solid',
              color: seriesColor,
            },
            itemStyle: {
              color: seriesColor,
            },
            cover: item.cover,
          });
        });
      } else {
      }
    } catch (e) {}

    return seriesTemp;
  };

  // 获取 lineXAxisData
  const getLineXAxisData = ({
    lineData,
    metric,
    xAxisAutoSort = true,
  }: {
    lineData: { [key: string]: any[] };
    metric: string;
    xAxisAutoSort?: boolean;
  }) => {
    if (!lineData?.hasOwnProperty(metric)) return [];
    const listTmp = lineData?.[metric] || [];
    const linexAxisSetTmp: Set<string> = new Set();
    listTmp?.forEach((item: any) => {
      item?.history?.forEach((subItem: any) => {
        const historyTime = subItem?.time;
        if (historyTime) {
          linexAxisSetTmp.add(historyTime);
        }
      });
    });
    // 按时间排序升序排序
    // return [...linexAxisSetTmp];
    return xAxisAutoSort
      ? getSortedDates(Array.from(linexAxisSetTmp), 'asc')
      : Array.from(linexAxisSetTmp);
  };

  const calcAxisInnerPadding = (text: string) => {
    if (!text) return 6;
    const normalizedLength = text
      .split('')
      .reduce((acc, char) => acc + (char.charCodeAt(0) > 255 ? 2 : 1), 0);
    const step = Math.ceil(normalizedLength / 6);
    return Math.min(16, 6 + step * 2);
  };

  const formatterSeries = (isStackLine?: boolean) => {
    const _seriesData =
      JSON.parse(
        JSON.stringify(
          lineSeriesData ? lineSeriesData : createLineSeriesItemData(lineData, metric),
        ),
      ) || [];

    return _seriesData?.map((_item: any, _index: number) => {
      // console.log('_item346435645', _item);
      let _obj: { [key: string]: any } = {};
      _obj.name = _item?.name || '-';
      _obj.id = _item?.id;
      // 显式启用时仅透传真数链路依赖的字段，避免其他 ECharts 配置改变公共图表行为。
      if (shouldPreserveSeriesDisplayMeta) {
        _obj.dataSourceKey = _item?.dataSourceKey;
        _obj.dbTooltipName = _item?.dbTooltipName;
        _obj.dbIsTrueData = _item?.dbIsTrueData;
        _obj.dbParentMetric = _item?.dbParentMetric;
      }
      _obj.step = _item?.step ?? false;
      _obj.type = 'line';
      // _obj.symbol = 'circle';
      _obj.data = _item?.data;
      _obj.cover = _item?.cover;
      // _obj.data = _item?.data;
      if (_item?.itemStyle && _item?.itemStyle?.color) {
        _obj.itemStyle = _item.itemStyle;
      } else {
        _obj.itemStyle = {
          color: colors[_index % colors.length],
        };
      }
      _obj.lineStyle = {
        ...(_item?.lineStyle || {}),
        color: _obj.itemStyle.color,
      };
      if (isStackLine) {
        _obj.areaStyle = {
          opacity: 0.3,
        };
        _obj.stack = 'Total';
      }

      if (baseReleaseTime !== '') {
        _obj.markLine = {
          symbol: ['none', 'none'],
          animation: false,
          itemStyle: {
            normal: {
              lineStyle: {
                type: 'dashed',
                color: '#0053B2',
                opacity: 0.54,
              },
            },
          },
          data: [
            {
              name: formatMessage({ id: 'dbi_since_release' }),
              xAxis: baseReleaseTime,
              label: {
                position: 'end',
                show: true,
                textStyle: {
                  color: '#0053B2',
                  fontSize: 12,
                  opacity: 0.54,
                },
                padding: [0, 0, -55, 0],
                formatter(item: any) {
                  return item.name;
                },
              },
            },
            {
              name: formatMessage({ id: 'dbi_earliest_data' }),
              xAxis: earliestPeriod,
              label: {
                position: 'end',
                show: true,
                textStyle: {
                  color: '#0053B2',
                  fontSize: 12,
                },
                padding: [0, 0, -25, 0],
                formatter(item: any) {
                  return item.name;
                },
              },
            },
          ],
        };
      }

      const isStepFlag = !!_obj.step;
      /** true 表示当前为业务页显式接入的可连线真数，需要保留菱形 marker。 */
      const isConnectedTrueDataSeries = Boolean(_obj.dbIsTrueData);
      const defaultSymbol = isStepFlag ? 'emptyCircle' : 'circle';
      const dataItemObj = {
        // showSymbol: true,
        showSymbol: _obj.step ? false : !(_item?.data?.length > 1),
        // 普通折线维持原来的 8px 圆点；仅真数曲线允许声明菱形尺寸和形状。
        symbolSize: isConnectedTrueDataSeries ? _item?.symbolSize ?? 8 : 8,
        symbol: isConnectedTrueDataSeries
          ? _item?.symbol ?? 'diamond'
          : defaultSymbol,
      };

      // 下架此需求
      // if (islowerGame && dateType.toLocaleLowerCase() == 'day' && hanPullOffTime(dateValue, lowerGameTime) && _index == 0) {
      //   const markTemp = {
      //     silent: true,
      //     itemStyle: {
      //       opacity: 0.5,
      //       color: "#f0f0f0"
      //     },
      //     label: {
      //       show: true,
      //       formatter: ['Off the Shelves'].join('\n'),
      //       color: "#9b9d98",
      //       position: 'inside',
      //       align: 'center',
      //       verticalAlign: 'middle',
      //       overflow: "break",
      //     },
      //     data: [
      //       [
      //         {
      //           xAxis: `${lowerGameTime[0]}T00:00:00+08:00`
      //         },
      //         {
      //           xAxis: `${moment(lowerGameTime[1]).valueOf() > moment(dateValue[1]).valueOf() ? dateValue[1] : lowerGameTime[1]}T00:00:00+08:00`
      //         }
      //       ]
      //     ],
      //   };
      //   _obj = { ..._obj, markArea: markTemp };
      // }

      _obj = { ..._obj, ...dataItemObj };

      // hover图例时，其他线条设置透明度
      if (legendHoverOpacity !== undefined) {
        const emphasisConfig: any = {
          focus: 'series',
          blurScope: 'coordinateSystem',
        };

        _obj.emphasis = emphasisConfig;

        _obj.blur = {
          lineStyle: {
            opacity: legendHoverOpacity,
          },
          itemStyle: {
            opacity: 0,
          },
        };
      }

      // console.log('_obj_obj_obj_obj', _obj);
      return _obj;
    });
  };

  // 图例信息
  const legendFormatter = (lineDataValue: any) => ({
    type: 'scroll',
    orient: 'horizontal', // 横向
    pageIconColor: '#000', // 翻页箭头颜色
    selectedMode: true,
    pageIconSize: 12,
    pageTextStyle: {
      color: '#000', // 翻页数字颜色
    }, // 翻页数字设置
    show: true,
    icon: 'circle',
    itemWidth: 8,
    itemHeight: 8,
    borderWidth: 0,
    // bottom: 0,
    bottom: LEGEND_BOTTOM,
    inactiveBorderWidth: 0,
    inactiveBorderColor: '#ffffff', // 默认存在#ccc，看起来会比设置的icon图例大小有区别
    inactiveColor: '#ccc', // 关闭时图例颜色
    left: 'center',
    textStyle: {
      rich: {
        a: {
          color: '#888888',
          fontSize: 12,
          // 确保文字与图标间距一致
          padding: [0, 0, 0, -4],
        },
        boldB: {
          color: '#262832',
          fontSize: 12,
          fontWeight: 'bold',
                  },
      },
    },
    itemGap: 20, // 图例之间间距20
    selected: legendSelected ? legendSelected : {},
    formatter: (name: any) => {
      let showText = '';
      let isHigh = false;
      if (lineDataValue?.hasOwnProperty(metric)) {
        const listTmp = lineDataValue?.[metric] || [];
        const showObj = listTmp?.find((v: any) => v?.name == name) || {};
        const showId = showObj?.id || '';
        showText = showObj?.name || name || '';
        showText = seriesNameFormatter?.(showText) ?? showText;
        isHigh = gameHighId !== '' && showId === gameHighId;
      }
      // 高亮legend标题
      return `{${isHigh ? 'boldB' : 'a'}| ${showText}}`;
    },
  });

  const lineOption = useMemo(() => {
    let options = {} as any;

    let yAxisTemp: any = null;

    let gameHighObj: any = {};
    if (lineData?.hasOwnProperty(metric)) {
      const listTmp = lineData?.[metric] || [];
      gameHighObj = listTmp?.find((v: any) => gameHighId !== '' && v?.id == gameHighId) || {};
    }

    const seriesTemp = handleSerieSymbolConfig(
      formatterSeries(isStackLine),
      getRealValueOfSerie,
      gameHighObj,
      specialSeriesOption,
    );

    let seriesListTemp = seriesTemp;

    if (isTwoYAxis && visuailsMetricValues?.length > 0) {
      const yAxisArrTemp: any = [];
      const seriesArrTemp: any = [];
      visuailsMetricValues?.forEach((item: any, index: number) => {
        let objYa = {} as any;
        const baseNameBottomPadding = 6;
        const isRightAxis = index > 0;
        const axisDisplayText = item?.text;
        const hideAxisTitle = useExternalAxisTitles;
        const innerPadding = hideAxisTitle ? 0 : calcAxisInnerPadding(axisDisplayText);
        objYa = {
          type: 'value',
          name: hideAxisTitle ? '' : axisDisplayText,
          splitNumber,
          splitLine: {
            show: !(index > 0),
            lineStyle: {
              type: 'dashed',
            },
          },
          axisLabel: {
            formatter:
              item?.renderType && item?.renderType === RenderType.GROWTH_ONLY
                ? formatterYAxisPercentLine
                : yAxisFormatter
                ? yAxisFormatter
                : formatterYAxis,
            // y轴文字样式
            // color: '#000',
            color: '#838697',
            // opacity: 0.45,
            fontSize: 12,
          },
          ...yAxisConfig,
        };

        if (!hideAxisTitle) {
          objYa = {
            ...objYa,
            // Show metric name at the top of each axis
            nameLocation: 'end',
            nameGap: 12,
            nameTextStyle: {
              align: isRightAxis ? 'right' : 'left',
              verticalAlign: 'bottom',
              padding: isRightAxis
                ? [0, innerPadding, baseNameBottomPadding, 0]
                : [0, 0, baseNameBottomPadding, innerPadding],
              color: '#888888',
              fontSize: 12,
              fontWeight: 500,
            },
          };
        }

        if ((item?.renderType && item?.renderType === RenderType.GROWTH_ONLY) || isPercentLine) {
          const maxConfig = isNaNeedMax ? { max: 100 } : {};
          objYa = {
            ...objYa,
            ...maxConfig,
            min: 0,
          };
        }
        yAxisArrTemp.push(objYa);

        if (Array.isArray(seriesTemp)) {
          const matchingSeries = seriesTemp.filter((subItem: any) =>
            seriesBelongsToAxisMetric(
              subItem?.name,
              // 真数使用独立 series id，必须先用父指标关系决定所属 Y 轴。
              subItem?.dbParentMetric ||
                subItem?.id ||
                subItem?.metricId,
              item,
            ),
          );
          matchingSeries.forEach((serieObj: any) => {
            const globalIndex = seriesTemp.indexOf(serieObj);
            const seriesColor =
              serieObj?.itemStyle?.color ||
              serieObj?.lineStyle?.color ||
              serieObj?.color ||
              colors[globalIndex >= 0 ? globalIndex : index] ||
              colors[index];
            seriesArrTemp.push({
              ...serieObj,
              yAxisIndex: index,
              color: seriesColor,
              itemStyle: {
                ...(serieObj?.itemStyle || {}),
                color: seriesColor,
              },
              lineStyle: {
                ...(serieObj?.lineStyle || {}),
                color: seriesColor,
              },
            });
          });
        }
      });
      yAxisTemp = yAxisArrTemp;
      seriesListTemp = seriesArrTemp;
    } else {
      let yAxisObjTemp = {
        type: 'value',
        splitNumber,
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
          },
        },
        axisLabel: {
          formatter: yAxisFormatter ? yAxisFormatter : formatterYAxis,
          // y轴文字样式
          // color: '#000',
          color: '#838697',
          // opacity: 0.45,
          fontSize: 12,
        },
        ...yAxisConfig,
      };

      if (isPercentLine) {
        const maxConfig = isNaNeedMax ? { max: 100 } : {};
        yAxisObjTemp = {
          ...yAxisObjTemp,
          ...maxConfig,
          min: 0,
        };
      }
      yAxisTemp = yAxisObjTemp;
    }

    // console.log('yAxisTemp~~~~~~~~~~~~~~', yAxisTemp);

    // --- Overlay TrueData as scatter and remove TrueData lines ---
    const TRUE_DATA_NAMES = ['databrain__units__truedata', 'databrain__revenue__truedata'];
    // Localized metric display names
    const METRIC_BY_TRUE_DATA: Record<string, string> = {
      databrain__units__truedata: formatMessage({ id: 'dbi_metric_lifetime_base_game_sales_units' }),
      databrain__revenue__truedata: formatMessage({ id: 'dbi_metric_base_game_sales_units' }),
    };
    // Localized label for the scatter overlay and legend
    const trueDataLabel = formatMessage({ id: 'dbi_true_data_points' });

    const makeScatter = (
      name: string,
      pts: any[],
      color?: string,
      parentName?: string,
    ): any => ({
      type: 'scatter',
      name: name || trueDataLabel,
      // custom link to the parent KPI line, used by ChartsIndex legend filtering
      dbParentName: parentName || '',
      data: (pts || [])
        .map((p: any) => {
          const tuple = Array.isArray(p)
            ? p
            : [
                p?.time ?? p?.x ?? (Array.isArray(p?.value) ? p?.value?.[0] : undefined),
                Array.isArray(p?.value) ? p?.value?.[1] : p?.value ?? p?.y,
              ];
          return {
            value: tuple, // [time, value]
            __isTrueData: true,
            __parentName: parentName || '',
            // enforce color at the point level to avoid palette/name interference
            itemStyle: { color: color || '#999999', borderColor: '#ffffff', borderWidth: 1 },
            emphasis: { itemStyle: { color: color || '#999999' } },
          };
        })
        // keep only valid points to ensure symbols render
        .filter((d: any) => d?.value?.[1] !== null && d?.value?.[1] !== undefined),
      symbol: 'diamond',
      symbolSize: 8,
      itemStyle: {
        color: color || '#999999',
        borderColor: '#ffffff',
        borderWidth: 1,
      },
      emphasis: { itemStyle: { color: color || '#999999' } },
      z: 10,
    } as any);

    // Find all true data series from the raw formatted series
    const trueDataSeries = Array.isArray(seriesTemp)
      ? seriesTemp.filter((s: any) => TRUE_DATA_NAMES.includes(s?.name))
      : [];

    // Build scatter series and attach to corresponding yAxis when dual-axis
    const scatterSeries: any[] = [];
    trueDataSeries.forEach((td: any) => {
      const targetMetricName = METRIC_BY_TRUE_DATA[td?.name as string];
      // Try to inherit color from the corresponding KPI line series
      let targetLine: any = null;
      let parentIndex = -1;
      if (Array.isArray(seriesListTemp)) {
        const nonScatter = seriesListTemp.filter((s: any) => s?.type !== 'scatter');
        parentIndex = nonScatter.findIndex((s: any) => s?.name === targetMetricName);
        targetLine = nonScatter[parentIndex];
      }
      // If no matching parent line is visible, skip rendering this True Data
      if (!targetLine || parentIndex < 0) {
        return;
      }
      const paletteColor = parentIndex >= 0 && Array.isArray(colors) ? colors[parentIndex] : undefined;
      let targetColor =
        (targetLine?.itemStyle && (targetLine.itemStyle as any).color) ||
        (targetLine?.lineStyle && (targetLine.lineStyle as any).color) ||
        paletteColor;
      // Fallback to original seriesTemp mapping when legend/dual-axis alters order
      if (!targetColor && Array.isArray(seriesTemp)) {
        const origIdx = seriesTemp.findIndex(
          (s: any) => s?.type !== 'scatter' && s?.name === targetMetricName,
        );
        const orig = seriesTemp[origIdx];
        targetColor = (orig?.itemStyle && (orig.itemStyle as any).color) || colors?.[origIdx];
      }
      if (!targetColor) targetColor = colors?.[0];
      const scatter = makeScatter(trueDataLabel, td?.data || [], targetColor, targetMetricName);
      // Map to correct yAxisIndex if dual-axis
      if (isTwoYAxis && Array.isArray(seriesListTemp)) {
        if (targetLine && typeof targetLine.yAxisIndex === 'number') {
          scatter.yAxisIndex = targetLine.yAxisIndex;
        } else if (
          isTwoYAxis &&
          Array.isArray(visuailsMetricValues) &&
          visuailsMetricValues.length > 0
        ) {
          const idx = (visuailsMetricValues as any[]).findIndex(
            (m: any) => m?.text === targetMetricName,
          );
          if (idx >= 0) scatter.yAxisIndex = idx;
        }
      }
      // Only push if there is valid data
      if (Array.isArray(scatter.data) && scatter.data.length > 0) {
        scatterSeries.push(scatter);
      }
    });

    // Remove TrueData line series from the final list
    if (Array.isArray(seriesListTemp)) {
      seriesListTemp = seriesListTemp.filter(
        (s: any) => !TRUE_DATA_NAMES.includes(s?.name),
      );
    }

    // Append scatter overlays. If there is no true data, scatterSeries will be empty.
    seriesListTemp = [...(seriesListTemp || []), ...scatterSeries];
    if (!isTwoYAxis) {
      // 显式重置为左轴，避免 ECharts 合并同 id series 时保留双轴状态下的 yAxisIndex: 1。
      seriesListTemp = seriesListTemp?.map((series: any) => {
        return {
          ...series,
          yAxisIndex: 0,
        };
      });
    }

    // Prepare legend: include the True Data Points scatter series for correct color handling
    const legendBase = legendsCustomMade ? legendsCustomMade : legendFormatter(lineData);
    const legendNames = (seriesListTemp || [])
      // Filter out all scatter series except the one for the legend
      .filter((s: any) => s?.type !== 'scatter' || s?.name === trueDataLabel)
      .map((s: any) => s?.name);
    const legendSet = new Set<string>(legendNames);
    // Build legend data as objects to reliably control icons
    const legendData: any[] = Array.from(legendSet).map((name) => ({
      name,
      // Ensure True Data Points shows as a diamond in the legend
      ...(name === trueDataLabel && { icon: 'diamond' }),
    }));
    // 预先计算 lineSourceIcon 的 URL（避免每次 formatter 调用都执行）
    const legendSourceIconUrl = lineSourceIcon
      ? typeof lineSourceIcon === 'string'
        ? getDataSourceIcon(lineSourceIcon)
        : lineSourceIcon
      : null;

    // 合并 textStyle.rich（保留默认样式，添加 sourceIcon）
    const mergedTextStyle = legendSourceIconUrl
      ? {
          textStyle: {
            ...(legendBase?.textStyle || {}),
            rich: {
              ...(legendBase?.textStyle?.rich || {}),
              sourceIcon: {
                height: 15,
                width: 15,
                backgroundColor: {
                  image: legendSourceIconUrl,
                },
              },
              a: {
                color: '#838697',
                fontSize: 12,
                padding: [0, 0, 0, 0],
              },
            },
          },
        }
      : {};

    const legendObj = {
      ...legendBase,
      data: legendData,
      // Ensure the legend label for True Data Points displays the name
      formatter: (name: any) => {
        if (name === trueDataLabel) {
          return `{a| ${trueDataLabel}}`;
        }
        // 如果配置了 lineSourceIcon，在 legend 文字前添加 icon
        if (legendSourceIconUrl) {
          return `{sourceIcon|}{a| ${name}}`;
        }
        if (typeof legendBase?.formatter === 'function') {
          try {
            return legendBase.formatter(name);
          } catch (e) {
            return name;
          }
        }
        return seriesNameFormatter?.(name) ?? name;
      },
      ...mergedTextStyle,
    } as any;

    options = {
      color: colors,
      tooltip: {
        trigger: 'axis',
        padding: 0,
        borderWidth: 0,
        formatter: tooltipFormatter ? tooltipFormatter : lineFormatterTooltip,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        ...tooltip,
      },
      grid: {
        left: '20px',
        right: isTwoYAxis ? '40px' : '30px',
        containLabel: true,
        ...gridConfig,
      },
      legend: legendObj,
      xAxis: {
        type: 'category',
        data: lineXAxisData ? lineXAxisData : getLineXAxisData({ lineData, metric, xAxisAutoSort }),
        axisTick: {
          alignWithLabel: true, // x轴刻度线与标签对齐
          lineStyle: {
            color: '#BFBFBF',
            // 透明度会展示到虚线，暂时用纯色代替
            // color: '#000',
            // opacity: 0.25,
            width: 1,
          },
        },
        // Flush edges with no extra gap at start/end
        boundaryGap: false,
        axisLabel: {
          formatter: xAxisLabelFormatter,
          // x轴文字样式
          color: '#000',
          align: 'center',
          opacity: 0.45,
          fontSize: 12,
          margin: 10,
        },
        axisLine: {
          show: true,
          onZero: false, // 当y轴有负数时,让x轴下落在y轴最小值
          lineStyle: {
            color: '#BFBFBF',
            // 透明度会展示到虚线，暂时用纯色代替
            // color: '#000',
            // opacity: 0.25,
            width: 1,
          },
        },
      },
      yAxis: yAxisTemp,
      series: seriesListTemp,
    };

    /**
     * @author lv.chen
     * @date 2025-10-27
     * @description 添加x轴hover高亮效果
     * 仅对有 x 轴数据的图表开启 hover 高亮，避免空类目轴触发 ECharts 兼容性异常。
     */
    const xAxisData = options?.xAxis?.data;
    if (xAxisData && xAxisData.length > 0) {
      options = xAxisHighLight(options);
    }

    if (setChartLoading && (chartType === 'line' || chartType === 'stackedLine'))
      setChartLoading(false);
    return options;
  }, [props]);

  return {
    lineOption,
  };
}
